/**
 * Centralized configuration for pages and items
 * - Icons: All icons go through IconProvider
 * - Labels: User-facing names
 * - Tooltips: Hover text
 * - Routes: Navigation paths
 */

import { FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type PageId = 
  | 'home'
  | 'templates'
  | 'library'
  | 'dialogs'
  | 'logic'
  | 'variables'
  | 'documents'
  | 'tms'
  | 'types-and-tags'
  | 'images'
  | 'ui'
  | 'relationship'
  | 'settings'
  | 'logout'
  | 'code-engine';

export type ItemType = 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';

export interface PageConfig {
  id: PageId;
  label: string;
  icon: string; // IconProvider iconstring
  tooltip: string;
  route: string;
  requiresGame?: boolean;
  requiresOwner?: boolean;
}

export interface ItemTypeConfig {
  type: ItemType;
  label: string;
  labelPlural: string;
  icon: string; // IconProvider iconstring
  tooltip: string;
  route: string;
  folderStructureType: FolderStructureType;
}

// ============================================================================
// PAGE CONFIGURATIONS
// ============================================================================

export const PAGE_CONFIGS: Record<PageId, PageConfig> = {
  home: {
    id: 'home',
    label: 'Home',
    icon: 'fa-houselucide',
    tooltip: 'Home',
    route: '/home',
  },
  templates: {
    id: 'templates',
    label: 'Templates',
    icon: 'fa-layout-panel-toplucide',
    tooltip: 'Templates',
    route: '/templates',
    requiresGame: true,
  },
  library: {
    id: 'library',
    label: 'Library',
    icon: 'fa-librarylucide',
    tooltip: 'Library',
    route: '/library',
    requiresGame: true,
  },
  dialogs: {
    id: 'dialogs',
    label: 'Dialogs',
    icon: 'fa-messages-squarelucide',
    tooltip: 'Conversation Dialogs',
    route: '/dialogs',
    requiresGame: true,
  },
  logic: {
    id: 'logic',
    label: 'Logic',
    icon: 'fa-workflowlucide',
    tooltip: 'Visual Logic Flow',
    route: '/logic',
    requiresGame: true,
  },
  variables: {
    id: 'variables',
    label: 'Variables',
    icon: 'fa-variablelucide',
    tooltip: 'Game Variables & Types with folders',
    route: '/variables',
    requiresGame: true,
  },
  documents: {
    id: 'documents',
    label: 'Documents',
    icon: 'fa-book-openlucide',
    tooltip: 'Documents',
    route: '/documents',
    requiresGame: true,
  },
  tms: {
    id: 'tms',
    label: 'Localization',
    icon: 'fa-languageslucide',
    tooltip: 'Translation Management System',
    route: '/tms',
    requiresGame: true,
  },
  'types-and-tags': {
    id: 'types-and-tags',
    label: 'Types and Tags',
    icon: 'fa-tagslucide',
    tooltip: 'Types and Tags',
    route: '/types-and-tags',
    requiresGame: true,
  },
  images: {
    id: 'images',
    label: 'Images',
    icon: 'fa-imagelucide',
    tooltip: 'Images',
    route: '/images',
    requiresGame: true,
  },
  ui: {
    id: 'ui',
    label: 'UI',
    icon: 'fa-blockslucide',
    tooltip: 'UI Elements',
    route: '/ui',
    requiresGame: true,
  },
  relationship: {
    id: 'relationship',
    label: 'Relationship',
    icon: 'fa-networklucide',
    tooltip: 'Relationship',
    route: '/relationship',
    requiresGame: true,
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    icon: 'fa-settingslucide',
    tooltip: 'Settings',
    route: '/settings',
    requiresGame: true,
  },
  logout: {
    id: 'logout',
    label: 'Logout',
    icon: 'fa-log-outlucide',
    tooltip: 'Logout',
    route: '', // Special handling for logout
  },
  'code-engine': {
    id: 'code-engine',
    label: 'Code Engine',
    icon: 'fa-code',
    tooltip: 'Template Code Engine (Dev Only)',
    route: '/code-engine',
    requiresGame: false,
  },
};

// ============================================================================
// ITEM TYPE CONFIGURATIONS
// ============================================================================

export const ITEM_TYPE_CONFIGS: Record<ItemType, ItemTypeConfig> = {
  glossary: {
    type: 'glossary',
    label: 'Object',
    labelPlural: 'Library',
    icon: 'fa-lines-leaning',
    tooltip: 'Library Object',
    route: '/library',
    folderStructureType: 'library',
  },
  document: {
    type: 'document',
    label: 'Document',
    labelPlural: 'Documents',
    icon: 'fa-file-text',
    tooltip: 'Document',
    route: '/documents',
    folderStructureType: 'documents',
  },
  blueprint: {
    type: 'blueprint',
    label: 'Template',
    labelPlural: 'Templates',
    icon: 'fa-file-lines',
    tooltip: 'Template',
    route: '/templates',
    folderStructureType: 'blueprints',
  },
  dialog: {
    type: 'dialog',
    label: 'Dialog',
    labelPlural: 'Dialogs',
    icon: 'fa-comments',
    tooltip: 'Conversation Dialog',
    route: '/dialogs',
    folderStructureType: 'dialogs',
  },
  flow: {
    type: 'flow',
    label: 'Logic Flow',
    labelPlural: 'Logic Flows',
    icon: 'fa-diagram-project',
    tooltip: 'Logic Flow',
    route: '/logic',
    folderStructureType: 'flows',
  },
  variable: {
    type: 'variable',
    label: 'Variable',
    labelPlural: 'Variables',
    icon: 'fa-variablelucide',
    tooltip: 'Game Variable',
    route: '/variables',
    folderStructureType: 'variables',
  },
};

// ============================================================================
// FOLDER STRUCTURE TYPE CONFIGURATIONS
// ============================================================================

export const FOLDER_TYPE_CONFIGS: Record<FolderStructureType, {
  label: string;
  icon: string;
  itemType: ItemType;
}> = {
  library: {
    label: 'Library',
    icon: 'fa-librarylucide',
    itemType: 'glossary',
  },
  documents: {
    label: 'Documents',
    icon: 'fa-book-openlucide',
    itemType: 'document',
  },
  blueprints: {
    label: 'Templates',
    icon: 'fa-layout-panel-toplucide',
    itemType: 'blueprint',
  },
  dialogs: {
    label: 'Dialogs',
    icon: 'fa-messages-squarelucide',
    itemType: 'dialog',
  },
  flows: {
    label: 'Logic Flows',
    icon: 'fa-workflowlucide',
    itemType: 'flow',
  },
  variables: {
    label: 'Variables',
    icon: 'fa-variablelucide',
    itemType: 'variable',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Page helpers
export function getPageConfig(pageId: PageId): PageConfig {
  return PAGE_CONFIGS[pageId];
}

export function getPageIcon(pageId: PageId): string {
  return PAGE_CONFIGS[pageId]?.icon || 'fa-question';
}

export function getPageLabel(pageId: PageId): string {
  return PAGE_CONFIGS[pageId]?.label || 'Unknown';
}

export function getPageTooltip(pageId: PageId): string {
  return PAGE_CONFIGS[pageId]?.tooltip || '';
}

export function getPageRoute(pageId: PageId): string {
  return PAGE_CONFIGS[pageId]?.route || '/';
}

// Item type helpers
export function getItemTypeConfig(itemType: ItemType): ItemTypeConfig {
  return ITEM_TYPE_CONFIGS[itemType];
}

export function getItemTypeIcon(itemType: ItemType): string {
  return ITEM_TYPE_CONFIGS[itemType]?.icon || 'fa-file';
}

export function getItemTypeLabel(itemType: ItemType, plural = false): string {
  const config = ITEM_TYPE_CONFIGS[itemType];
  if (!config) return 'Unknown';
  return plural ? config.labelPlural : config.label;
}

export function getItemTypeTooltip(itemType: ItemType): string {
  return ITEM_TYPE_CONFIGS[itemType]?.tooltip || '';
}

export function getItemTypeRoute(itemType: ItemType): string {
  return ITEM_TYPE_CONFIGS[itemType]?.route || '/';
}

// Folder structure type helpers
export function getFolderTypeConfig(folderType: FolderStructureType): {
  label: string;
  icon: string;
  itemType: ItemType;
} {
  return FOLDER_TYPE_CONFIGS[folderType];
}

export function getFolderTypeIcon(folderType: FolderStructureType): string {
  return FOLDER_TYPE_CONFIGS[folderType]?.icon || 'fa-folder';
}

export function getFolderTypeLabel(folderType: FolderStructureType): string {
  return FOLDER_TYPE_CONFIGS[folderType]?.label || 'Unknown';
}

export function getFolderTypeItemType(folderType: FolderStructureType): ItemType {
  return FOLDER_TYPE_CONFIGS[folderType]?.itemType || 'glossary';
}

// Conversion helpers
export function itemTypeToFolderType(itemType: ItemType): FolderStructureType {
  return ITEM_TYPE_CONFIGS[itemType]?.folderStructureType || 'library';
}

export function folderTypeToItemType(folderType: FolderStructureType): ItemType {
  return FOLDER_TYPE_CONFIGS[folderType]?.itemType || 'glossary';
}

// Entity type helper (for backwards compatibility)
export function getEntityIcon(entityType: string): string {
  switch (entityType) {
    case 'glossary':
    case 'document':
    case 'blueprint':
    case 'dialog':
    case 'flow':
    case 'variable':
      return getItemTypeIcon(entityType as ItemType);
    case 'type':
      return 'fa-tagslucide';
    default:
      return 'fa-file';
  }
}

export function getEntityLabel(entityType: string, plural = false): string {
  switch (entityType) {
    case 'glossary':
    case 'document':
    case 'blueprint':
    case 'dialog':
    case 'flow':
    case 'variable':
      return getItemTypeLabel(entityType as ItemType, plural);
    case 'type':
      return plural ? 'Types' : 'Type';
    default:
      return 'Unknown';
  }
}
